import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Files, Plus, RefreshCw } from 'lucide-react';
import { FILING_STATUSES, isInFlight, type EFileStatus, type Upload } from '@tabservice/tabfile-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/field';
import { TablePagination, TableToolbar, ToolbarField } from '@/components/ui/table';
import type { SortState } from '@/components/ui/table';
import { EmptyState, ErrorNote, ListSkeleton, Skeleton, TableSkeleton } from '@/components/ui/feedback';
import { FilingsTable } from '@/features/filings/filings-table';
import { draftUploadsQuery, filingsPageQuery, formsQuery } from '@/lib/queries';
import { DraftsTable, isDraftBeingChecked } from '@/features/filings/drafts-table';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { StatusFilter } from '@/features/filings/status-chips';
import { useToast } from '@/components/ui/toast';
import { tabfile } from '@/lib/api';
import { uploadManager } from '@/lib/upload-manager';
import { readStoredTaxYear, resolveTaxYear, taxYearsFromForms, useTaxYear } from '@/lib/tax-year';
import { useTour } from '@/tour/use-tour';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';

const searchSchema = z.object({
  status: z.enum(FILING_STATUSES as unknown as [EFileStatus, ...EFileStatus[]]).optional(),
  q: z.string().optional(),
  /** Which view the header's segmented control is showing. Absent means e-files. */
  view: z.enum(['drafts']).optional(),
  /**
   * Which column the table is ordered by, as Tab Service takes it: `records:asc`.
   *
   * In the URL because an order is part of what somebody is looking at. Sending a colleague
   * "the ones with the most records" should be a matter of sending them the address bar.
   */
  sort: z.string().optional(),
  /** Deep link for demos: /e-files?tour=customer starts the tour on arrival. */
  tour: z.enum(['customer', 'admin']).optional(),
});

export const Route = createFileRoute('/_app/e-files/')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <ListSkeleton rows={8} columns={7} />,
  validateSearch: searchSchema,
  // Deliberately NOT keyed on the status or the search term. Those live in the URL, and
  // making them loader dependencies re-runs this loader on every filter change - which
  // suspends the route and blanks the whole page to change one table. Filtering is the
  // component's job: the query keeps the previous rows while the next ones arrive, and the
  // table shows its own busy state over them.
  loaderDeps: () => ({}),
  loader: async ({ context }) => {
    // The year the header will land on decides which e-files are worth prefetching. When
    // the browser remembers a year, both requests go out together; only a first visit has
    // to wait for the form list to learn which years exist.
    const stored = readStoredTaxYear();
    // The unfiltered first page. A deep link with ?status= fetches its own on arrival.
    const filter = { status: undefined, q: undefined };
    const formsPending = context.queryClient.ensureQueryData(formsQuery());
    if (stored) {
      const [forms] = await Promise.all([
        formsPending,
        context.queryClient.ensureQueryData(filingsPageQuery({ ...filter, taxYear: stored, page: 1, pageSize: PAGE_SIZE })),
      ]);
      const taxYear = resolveTaxYear(stored, taxYearsFromForms(forms.forms));
      if (taxYear === stored) return;
      return context.queryClient.ensureQueryData(filingsPageQuery({ ...filter, taxYear, page: 1, pageSize: PAGE_SIZE }));
    }
    const forms = await formsPending;
    const taxYear = resolveTaxYear(stored, taxYearsFromForms(forms.forms));
    return context.queryClient.ensureQueryData(filingsPageQuery({ ...filter, taxYear, page: 1, pageSize: PAGE_SIZE }));
  },
  component: FilingsPage,
});

const PAGE_SIZE = 10;

/**
 * `records:asc` as the table's own sort state, and back again.
 *
 * The URL holds what Tab Service is asked for, not a shape of its own: one spelling for the
 * order means the link, the request and the heading glyph cannot drift apart.
 */
function parseSort(value: string | undefined): SortState | null {
  if (!value) return null;
  const [key, direction] = value.split(':');
  if (!key) return null;
  return { key, direction: direction === 'asc' ? 'asc' : 'desc' };
}

/** Ascending, then descending, then back to the default order. */
function nextSort(current: SortState | null, key: string): string | undefined {
  if (current?.key !== key) return `${key}:asc`;
  return current.direction === 'asc' ? `${key}:desc` : undefined;
}

function FilingsPage() {
  const navigate = Route.useNavigate();
  const openFiling = useNavigate();
  const { status, q, view, sort: sortParam, tour: tourParam } = Route.useSearch();
  const { taxYear } = useTaxYear();
  const tour = useTour();

  // Tab Service searches, filters and pages; the table shows exactly the page it returned.
  // Every page also carries the counts by status for the year and search, ignoring the
  // status filter, so the chips stay right while the table is narrowed to one status.
  const term = (q ?? '').trim();
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [taxYear, status, term, sortParam]);
  const sort = useMemo(() => parseSort(sortParam), [sortParam]);
  const list = useQuery({
    ...filingsPageQuery({ taxYear, status, q: term || undefined, sort: sortParam, page, pageSize: PAGE_SIZE }),
    refetchInterval: (item) => (item.state.data?.items.some((filing) => isInFlight(filing.status)) ? 15_000 : false),
  });
  const rows = list.data?.items ?? [];
  const statusCounts = useMemo(() => list.data?.statusCounts ?? {}, [list.data]);
  const yearTotal = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
  const total = list.data?.total ?? rows.length;

  // Drafts: uploads for this year that have not become an e-file yet.
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draftPage, setDraftPage] = useState(1);
  // Drafts are their own list with their own paging; only the year narrows them.
  useEffect(() => {
    setDraftPage(1);
  }, [taxYear]);
  // A draft whose file is still being checked moves on its own: to ready, to blocked, or into
  // the list above as an e-file. Ask again while one is, the way the e-files themselves are.
  const drafts = useQuery({
    ...draftUploadsQuery(taxYear, draftPage, PAGE_SIZE),
    refetchInterval: (item) => (item.state.data?.items.some(isDraftBeingChecked) ? 5_000 : false),
  });
  // A draft that leaves the list has usually just become an e-file, so the list above is stale.
  const draftIds = (drafts.data?.items ?? []).map((upload) => upload.id).join(',');
  const seenDraftIds = useRef(draftIds);
  useEffect(() => {
    const before = seenDraftIds.current.split(',').filter(Boolean);
    seenDraftIds.current = draftIds;
    const now = new Set(draftIds.split(',').filter(Boolean));
    if (before.some((id) => !now.has(id))) void queryClient.invalidateQueries({ queryKey: ['e-files'] });
  }, [draftIds, queryClient]);
  const discard = useMutation({
    mutationFn: (uploadId: string) => tabfile.DELETE('/v1/uploads/{uploadId}', { params: { path: { uploadId } } }),
    onSuccess: (_result, uploadId) => {
      uploadManager.dismiss(uploadId);
      toast.success('Draft discarded');
      void queryClient.invalidateQueries({ queryKey: ['uploads'] });
    },
    onError: (error) => toast.error('Could not discard the draft', errorMessage(error)),
  });

  // What is typed goes to the URL after a short pause, and from there to Tab Service.
  const [draft, setDraft] = useState(q ?? '');
  useEffect(() => {
    setDraft(q ?? '');
  }, [q]);
  useEffect(() => {
    const next = draft.trim() || undefined;
    if (next === (q?.trim() || undefined)) return;
    const timer = window.setTimeout(
      () => void navigate({ search: (old) => ({ ...old, q: next }), replace: true }),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [draft, navigate, q]);

  // ?tour= is a one shot: start it, then take the parameter back out of the URL. It is also
  // the only way into a tour now that nothing offers one on screen - the tours themselves are
  // still here, and a link with ?tour=customer still runs one.
  const tourStarted = useRef(false);
  useEffect(() => {
    if (!tourParam || tourStarted.current) return;
    tourStarted.current = true;
    void navigate({ search: (old) => ({ ...old, tour: undefined }), replace: true });
    tour.start(tourParam);
  }, [navigate, tour, tourParam]);

  function setStatus(next: EFileStatus | undefined) {
    void navigate({ search: (old) => ({ ...old, status: next }) });
  }

  function toggleSort(key: string) {
    void navigate({ search: (old) => ({ ...old, sort: nextSort(sort, key) }) });
  }

  const draftItems = drafts.data?.items ?? [];
  const draftTotal = drafts.data?.total ?? draftItems.length;
  // The draft waiting to be discarded, held while the confirmation is up. Discarding is not
  // undoable and a trash icon sits one row away from the row somebody meant to open.
  const [discardTarget, setDiscardTarget] = useState<Upload | null>(null);

  /**
   * Where a draft is picked up again.
   *
   * A draft started as a correction or a replacement goes back to that e-file's amend page;
   * sending it to the new-e-file wizard is what filed it as a separate e-file of its own
   * (TS-8288). One copy of that rule, used by the list and by the tab.
   */
  function resumeDraft(upload: Upload) {
    if (upload.amendsEfileId && upload.amendKind) {
      void openFiling({
        to: upload.amendKind === 'correction' ? '/e-files/$efileId/correct' : '/e-files/$efileId/replace',
        params: { efileId: upload.amendsEfileId },
        search: { uploadId: upload.id },
      });
      return;
    }
    void openFiling({ to: '/e-files/new', search: { uploadId: upload.id } });
  }

  return (
    <Card>
      {/*
        E-files and drafts are two views of one page, and the control that switches them sits
        in the header beside the breadcrumb - the same place Settings and Admin put theirs.
        The card holds only the panels; which one shows comes from the URL, so the view can
        be linked to and survives a reload.
      */}
      <Tabs value={view === 'drafts' ? 'drafts' : 'e-files'}>
        <TabsContent value="e-files">

      {/*
        The chips sit above the toolbar, on their own line: they are what the table is showing,
        not one more control for narrowing it, and they carry the shape of the year at a glance
        - how much is accepted, how much is still out, how much came back. They replace the
        status select that used to sit in the toolbar, which held the same eight statuses one
        at a time behind a click.
      */}

      <TableToolbar
        search={
          <SearchInput
            value={draft}
            aria-label="Search e-files"
            placeholder="Search by file name, form or reference"
            onChange={(event) => setDraft(event.target.value)}
          />
        }
      >
        {/*
          The status filter sits with the other controls rather than on a row of its own.
          Its counts arrive with the list, so it shows a placeholder of the same height
          until they do rather than appearing late and moving the table down the page.
        */}
        {list.isPending ? (
          <ToolbarField label="Status" className="w-[220px]">
            <Skeleton className="h-10 rounded-md" />
          </ToolbarField>
        ) : (
          <ToolbarField label="Status" className="w-[220px]">
            <StatusFilter counts={statusCounts} total={yearTotal} active={status} onSelect={setStatus} />
          </ToolbarField>
        )}
        <Button variant="outline" onClick={() => void list.refetch()}>
          <RefreshCw className={cn('h-4 w-4', list.isFetching && 'motion-safe:animate-spin')} aria-hidden="true" />
          Refresh
        </Button>
        <Button asChild data-tour="new-e-file">
          <Link to="/e-files/new">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New e-file
          </Link>
        </Button>
      </TableToolbar>

      {list.isError ? (
        <ErrorNote
          title="Could not load e-files"
          detail={errorMessage(list.error)}
          action={
            <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          }
        />
      ) : list.isPending || list.isPlaceholderData ? (
        // isPlaceholderData means these rows belong to the PREVIOUS filter: the query keeps
        // them so the page does not jump, but showing one status's rows under another
        // status's heading would be a lie. A skeleton says "fetching" without saying
        // anything false, and only the table changes - the chips, toolbar and tabs stay put.
        <TableSkeleton rows={8} columns={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Files}
          title={term || status ? 'No e-files match the filters' : `No e-files for ${taxYear}`}
          description={
            term || status
              ? 'Clear the search and the status filter to see every e-file for this year.'
              : 'Change the tax year in the header, or upload a file to submit a batch.'
          }
          action={
            term || status ? (
              <Button variant="outline" onClick={() => void navigate({ search: {} })}>
                Clear filters
              </Button>
            ) : (
              <Button asChild variant="primary">
                <Link to="/e-files/new">New e-file</Link>
              </Button>
            )
          }
        />
      ) : (
        <>
          <FilingsTable
            busy={list.isFetching}
            items={rows}
            sort={sort}
            onSort={toggleSort}
            onOpen={(filingId) => void openFiling({ to: '/e-files/$efileId', params: { efileId: filingId } })}
          />
          <TablePagination page={page} pageSize={PAGE_SIZE} total={total} busy={list.isFetching} onPageChange={setPage} />
        </>
      )}
        </TabsContent>

        <TabsContent value="drafts">
          {drafts.isPending ? (
            <TableSkeleton rows={4} columns={4} />
          ) : draftItems.length === 0 ? (
            <EmptyState
              icon={Files}
              title={`No drafts for ${taxYear}`}
              description="A draft is an e-file that was started and not submitted. Start one and it waits here until you finish it."
              action={
                <Button asChild variant="primary">
                  <Link to="/e-files/new">New e-file</Link>
                </Button>
              }
            />
          ) : (
            <>
              <DraftsTable
                busy={drafts.isFetching}
                items={draftItems}
                discarding={discard.isPending ? (discard.variables ?? null) : null}
                onOpen={resumeDraft}
                onDiscard={(uploadId) => {
                  const upload = draftItems.find((item) => item.id === uploadId);
                  if (upload) setDiscardTarget(upload);
                }}
              />
              <TablePagination
                page={draftPage}
                pageSize={PAGE_SIZE}
                total={draftTotal}
                busy={drafts.isFetching}
                onPageChange={setDraftPage}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={discardTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDiscardTarget(null);
        }}
        title="Discard this draft?"
        description={
          <>
            {discardTarget?.fileName ? (
              <>
                <span className="font-medium text-gray-3">{discardTarget.fileName}</span> has not been submitted, so
                nothing about it has reached the IRS.
              </>
            ) : (
              'This draft has not been submitted, so nothing about it has reached the IRS.'
            )}{' '}
            Discarding takes it off your list and cannot be undone. Open it instead to carry on from where it stopped.
          </>
        }
        confirmLabel="Discard draft"
        destructive
        pending={discard.isPending}
        onConfirm={() => {
          if (!discardTarget) return;
          discard.mutate(discardTarget.id);
          setDiscardTarget(null);
        }}
      />
    </Card>
  );
}
